/* eslint-disable max-lines-per-function */

/* eslint-disable complexity */
import React from 'react';

import { Button } from '@qality/common-components/src/atoms/Button';
import { Flex } from '@qality/common-components/src/atoms/Flex';
import {
  Table,
  createColumnWidths,
  createColumnsWidths,
} from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import { ReportBugsProvider } from '@qality/common-components/src/lib/ReportBugsProvider';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { FailedStep, TestExecution } from '@qality/common-model';
import {
  DateFormatService,
  StorageOfUniqueComponents,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { StringUtils } from '@qality/common-utils';

import { FailedStepsStyled, TestCycleLinkStyled } from './FailedSteps.styled';

type Columns =
  | 'createdOn'
  | 'testCycle'
  | 'failedSteps'
  | 'comment'
  | 'bugsReported'
  | 'actions';

const ColumnsWidths = createColumnsWidths<Columns>({
  createdOn: createColumnWidths('150px'),
  testCycle: createColumnWidths('150px', '120px'),
  failedSteps: createColumnWidths('auto', '100px'),
  comment: createColumnWidths('auto', '100px'),
  bugsReported: createColumnWidths('100px', '100px'),
  actions: createColumnWidths('150px'),
});

type FailedStepsViewProps = {
  steps: Array<FailedStep>;
  isLoading: boolean;
  openExecution: (executionId: TestExecution['id']) => void;
};

export const FailedStepsView: React.FC<FailedStepsViewProps> = ({
  steps,
  isLoading,
  openExecution,
}: FailedStepsViewProps) => {
  // TODO: get rid of StorageOfUniqueComponents
  const BugsTableCell = StorageOfUniqueComponents.get('BugsTableCell');
  return (
    <FailedStepsStyled>
      <Table>
        <Table.Head>
          <Table.Tr>
            <Table.Th widths={ColumnsWidths.createdOn}>Created on</Table.Th>
            <Table.Th widths={ColumnsWidths.testCycle}>Test Cycle</Table.Th>
            <Table.Th widths={ColumnsWidths.failedSteps}>Failed Step</Table.Th>
            <Table.Th widths={ColumnsWidths.comment}>Comment</Table.Th>
            <Table.Th widths={ColumnsWidths.bugsReported}>
              Bugs reported
            </Table.Th>
            <Table.Th widths={ColumnsWidths.actions}>Actions</Table.Th>
          </Table.Tr>
        </Table.Head>
        <Table.Body>
          {isLoading && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Flex justifyCenter paddingSize='small'>
                  <SpinnerLoader text='We are loading data...' />
                </Flex>
              </Table.Td>
            </Table.Tr>
          )}
          {!isLoading && steps.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Flex justifyCenter paddingSize='medium'>
                  <Text>There is no data to display here</Text>
                </Flex>
              </Table.Td>
            </Table.Tr>
          )}
          {!isLoading &&
            steps.length > 0 &&
            steps.map((step, index) => {
              const { testStep, testExecution } = step;
              return (
                <Table.Tr isHoverable key={index}>
                  <Table.Td>
                    <Table.ContentHelper alignItems='center'>
                      {testExecution.createdDate ? (
                        <Text>
                          {DateFormatService.formatDate(
                            testExecution.createdDate,
                            'longDateFormat'
                          )}
                        </Text>
                      ) : (
                        <Text inactive>None</Text>
                      )}
                    </Table.ContentHelper>
                  </Table.Td>
                  <Table.Td>
                    <Table.ContentHelper alignItems='center'>
                      {testExecution.testCycle.id ? (
                        <TestCycleLinkStyled
                          href={StorageOfUniqueServices.get(
                            'URLService'
                          ).getTestCycleUrl(testExecution.testCycle.id)}
                          target='_BLANK'
                        >
                          {testExecution.testCycle.name}
                        </TestCycleLinkStyled>
                      ) : (
                        <Text inactive>Ad hoc</Text>
                      )}
                    </Table.ContentHelper>
                  </Table.Td>
                  <Table.Td>
                    <Table.ContentHelper alignItems='center'>
                      {testStep.name ? (
                        <div
                          className='text-wrapper'
                          dangerouslySetInnerHTML={{
                            __html: StringUtils.toHTML(testStep.name),
                          }}
                        />
                      ) : (
                        <Text inactive>None</Text>
                      )}
                    </Table.ContentHelper>
                  </Table.Td>
                  <Table.Td>
                    <Table.ContentHelper alignItems='center'>
                      {testStep.comment ? (
                        <div
                          className='text-wrapper'
                          dangerouslySetInnerHTML={{
                            __html: StringUtils.toHTML(testStep.comment),
                          }}
                        />
                      ) : (
                        <Text inactive>None</Text>
                      )}
                    </Table.ContentHelper>
                  </Table.Td>
                  <Table.Td>
                    <Table.ContentHelper alignItems='center'>
                      <ReportBugsProvider linkedIssues={testStep?.linkedIssues}>
                        <BugsTableCell readOnly />
                      </ReportBugsProvider>
                    </Table.ContentHelper>
                  </Table.Td>
                  <Table.Td className='show-on-row-hover'>
                    <Table.ContentHelper alignItems='center'>
                      <Button
                        marginSize='small'
                        onClick={() => openExecution(testExecution.id)}
                      >
                        Open Execution
                      </Button>
                    </Table.ContentHelper>
                  </Table.Td>
                </Table.Tr>
              );
            })}
        </Table.Body>
      </Table>
    </FailedStepsStyled>
  );
};
